export type FirebaseAuthMessageContext =
  | 'login'
  | 'register'
  | 'google'
  | 'passwordReset'
  | 'passwordChange'
  | 'reauthenticate'
  | 'default';

export function getFirebaseAuthErrorCode(err: unknown): string {
  if (err && typeof err === 'object' && 'code' in err) {
    return String((err as { code?: unknown }).code ?? '');
  }
  return '';
}

function getFirebaseAuthRawMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (err && typeof err === 'object' && 'message' in err) {
    return String((err as { message?: unknown }).message ?? '');
  }
  return '';
}

export function getFirebaseAuthErrorMessage(
  err: unknown,
  context: FirebaseAuthMessageContext = 'default'
): string {
  const code = getFirebaseAuthErrorCode(err);
  const rawMessage = getFirebaseAuthRawMessage(err);
  const diagnosticText = `${code} ${rawMessage}`.toLowerCase();

  if (diagnosticText.includes('requests-from-referer') || diagnosticText.includes('are blocked')) {
    return 'A chave do Firebase bloqueou este domínio. Verifique os sites permitidos no Google Cloud e tente novamente.';
  }

  if (context === 'reauthenticate' && (code === 'auth/wrong-password' || code === 'auth/invalid-credential')) {
    return 'Senha atual incorreta.';
  }

  if (context === 'passwordReset' && (code === 'auth/expired-action-code' || code === 'auth/invalid-action-code')) {
    return 'Link inválido ou expirado. Solicite uma nova redefinição de senha.';
  }

  switch (code) {
    case 'auth/email-already-in-use':
      return 'Este e-mail já está cadastrado. Faça login ou use outro e-mail.';
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'E-mail ou senha incorretos. Verifique e tente novamente.';
    case 'auth/invalid-email':
      return 'Informe um e-mail válido.';
    case 'auth/weak-password':
      return 'A senha deve ter pelo menos 8 caracteres.';
    case 'auth/missing-password':
      return 'Informe sua senha.';
    case 'auth/user-disabled':
      return 'Esta conta foi desativada. Entre em contato com o suporte.';
    case 'auth/too-many-requests':
      return 'Muitas tentativas em pouco tempo. Aguarde alguns minutos e tente novamente.';
    case 'auth/quota-exceeded':
      return 'Aguarde alguns minutos antes de tentar novamente.';
    case 'auth/network-request-failed':
      return 'Falha de conexão. Verifique sua internet e tente novamente.';
    case 'auth/popup-blocked':
      return 'Popup bloqueado. Permita popups para este site ou use e-mail e senha.';
    case 'auth/cancelled-popup-request':
      return 'Já existe uma tentativa de login com Google em andamento.';
    case 'auth/account-exists-with-different-credential':
    case 'auth/credential-already-in-use':
      return 'Já existe uma conta com este e-mail usando outro método de login.';
    case 'auth/requires-recent-login':
      return 'Por segurança, faça logout e login novamente antes de continuar.';
    case 'auth/operation-not-allowed':
      return 'Este método de login não está habilitado. Verifique a configuração do Firebase.';
    case 'auth/unauthorized-domain':
      return 'Este domínio não está autorizado no Firebase. Adicione o domínio nas configurações de autenticação.';
    case 'auth/auth-domain-config-required':
    case 'auth/invalid-api-key':
    case 'auth/app-not-authorized':
    case 'auth/api-key-not-valid':
      return 'Firebase não configurado corretamente. Verifique as variáveis de ambiente e os domínios autorizados.';
    case 'auth/internal-error':
      return 'Erro interno do Firebase. Verifique se a chave permite a Identity Toolkit API e os sites autorizados.';
    case 'auth/expired-action-code':
    case 'auth/invalid-action-code':
      return 'Link inválido ou expirado. Solicite um novo link.';
    default:
      break;
  }

  const localDiagnostic =
    typeof import.meta !== 'undefined' && import.meta.env?.DEV && (code || rawMessage)
      ? ` (${code || rawMessage})`
      : '';

  if (context === 'register') return `Não foi possível concluir o cadastro. Tente novamente.${localDiagnostic}`;
  if (context === 'google') return `Não foi possível entrar com Google. Tente novamente.${localDiagnostic}`;
  if (context === 'passwordReset') return `Não foi possível enviar o link de recuperação. Tente novamente.${localDiagnostic}`;
  if (context === 'passwordChange') return `Não foi possível alterar a senha. Tente novamente.${localDiagnostic}`;
  if (context === 'login') return `Não foi possível entrar. Tente novamente.${localDiagnostic}`;
  return 'Ocorreu um erro. Tente novamente.';
}
